<template>
  <TheNavbar />

  <div class="mt-4 mb-3">
    <TheSubscriptionAlert />
  </div>

  <div class="mb-5">
    <div class="row justify-content-center mb-3">
      <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">

        <!-- PRO Feature Info -->
        <div class="alert alert-primary border-0 d-flex align-items-center mb-3" role="alert">
          <div class="bg-info me-3 icon-item"><span class="far fa-gem text-white fs-6"></span></div>
          <p class="mb-0 flex-1">
            The Trend Radar is a <span class="fw-bold">Pro</span> <span class="far fa-gem"></span> feature. You currently have free access during our Beta phase (yay) 😎<br>
            Once we launch our <span class="fw-bold">Pro Plan</span> (more Bots, MCP, API), Trend Radar will be available only in the Pro Plan (it's too good for less).
          </p>
        </div> 

        <!-- Asset Class Tabs -->
        <ul class="nav nav-tabs mb-3">
          <li v-for="ac in allAssetClasses" :key="ac" class="nav-item">
            <a class="nav-link"
               :class="{ active: selectedAssetClass === ac }"
               @click.prevent="selectAssetClass(ac)" href="#">
              {{ formatAssetClass(ac) }}
            </a>
          </li>
        </ul>

        <!-- Detector Toggle -->
        <div class="mb-3">
          <div class="btn-group btn-group-sm">
            <button v-for="d in allDetectors" :key="d"
                    class="btn"
                    :class="selectedDetector === d ? 'btn-primary' : 'btn-outline-primary'"
                    @click="selectDetector(d)">
              <span v-if="d == 'gc'">GaussianChannel</span>
              <span v-if="d == 'b2s'">Bot2Slow <span class="fas fa-crown" title="AutoTrading VIP only"></span></span>
            </button>
          </div>

          <div v-if="!showTeaser && !showNoDataMessage">
            <p v-if="selectedDetector == 'gc'" class="text-muted mt-2">With the <a href="https://www.tradingview.com/script/Ku2S5zvW-GaussianChannel-Strategy-v3-3/" target="_blank">GaussianChannel Trend Indicator <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> we scan the <span class="fw-bold">top {{ assetsPerAssetClassHtml }} assets</span> by market cap daily to identify emerging Uptrends (Green) and Downtrends (Red) - so you can spot opportunities early.</p>
            <p v-if="selectedDetector == 'b2s'" class="text-muted mt-2">
              With the <a href="https://www.tradingview.com/script/6xiIO9QY-Bot2Slow-Stocks-v1-x-AT-X/" target="_blank">Bot2Slow Trend Indicator <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span></a> we scan the <span class="fw-bold">top {{ assetsPerAssetClassHtml }} assets</span> by market cap daily to identify emerging Uptrends (Green) and Downtrends (Red) - so you can spot opportunities early.<br>
              <a v-if="!b2sExpanded" @click.prevent="b2sExpanded = true" href="#" class="text-primary fs-9">Show more...</a>
            </p>
            <div v-if="selectedDetector == 'b2s'" v-show="b2sExpanded" class="text-muted mb-0">
              <p>
                Grey stands for "undecided", which means that the asset is not clearly in an uptrend or downtrend (no trade zone).<br>
                The "Range" column indicates that the Trend Radar AI has detected a technical range, which indicates additional risk even if the asset is trending up/down.<br>
              </p>
              <p>
                The ideal scenario is a recent Trend Flip with no Range indication.
              </p>
            </div>
            <hr>
          </div>
        </div>

        <!-- No data for this combo (e.g. GC on Stocks) -->
        <div v-if="showNoDataMessage" class="alert alert-primary border-0 d-flex align-items-center" role="alert">
          <div class="bg-info me-3 icon-item"><span class="fas fa-info-circle text-white fs-6"></span></div>
          <p class="mb-0 flex-1">The <span class="fw-bold">{{ selectedDetector === 'gc' ? 'GaussianChannel' : 'Bot2Slow' }}</span> detector is not available for <span class="fw-bold">{{ formatAssetClass(selectedAssetClass) }}</span>. Please select a different detector or asset class.</p>
        </div>

        <!-- AutoTrading Teaser -->
        <div v-if="showTeaser" class="row justify-content-center mb-4">
          <div class="col-sm-12 col-md-10 col-lg-8 col-xl-8 col-xxl-6">
            <div class="card">
              <div class="card-header">
                <h5 class="mb-0">AutoTrading Masterclass</h5>
              </div>
              <div class="card-body bg-body-tertiary">
                <img class="img-fluid rounded mx-auto mb-3" src="@/assets/strategies/at_course_cover.jpg" />
                <p>
                  The <span class="fw-bold">Bot2Slow Trend Detector</span> and additional asset classes like <span class="fw-bold">Stocks, Metals, Commodities</span> are exclusively available for <span class="fw-bold">AutoTrading VIP members</span>.
                </p>
                <div class="text-center">
                  <a class="btn btn-primary btn-sm" type="button" href="https://autotrading.vip" target="_blank">
                    Learn More <span class="fas fa-external-link-alt" data-fa-transform="shrink-3 down-0"></span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Next Update Countdown -->
        <div v-if="!showTeaser && !showNoDataMessage" class="d-flex align-items-center text-muted mb-3">
          <span v-if="dailyRunDate" class="me-3"><span class="fas fa-check-square me-1"></span>Last updated: <span class="fw-bold">{{ lastUpdatedText }}</span></span>
          <span><span class="fas fa-clock me-1"></span>Next update in: <span class="fw-bold">{{ countdownText }}</span></span>
        </div>

        <!-- Chart (always mounted, data swaps reactively) -->
        <TrendRadarChart :class="{ 'd-none': showTeaser || showNoDataMessage || historicData.length === 0 }" :data="historicData.length > 0 ? historicData : placeholderData" :detector="selectedDetector" />

        <!-- Trend Radar Table -->
        <div v-if="!showTeaser && !showNoDataMessage">
          <TrendRadarTable :results="dailyResults" :runDate="dailyRunDate" :detector="selectedDetector" />

          <!-- Subscribe CTA for inactive subscriptions -->
          <div v-if="isLimited" class="alert alert-warning border-0 d-flex align-items-center mt-3" role="alert">
            <div class="bg-warning me-3 icon-item"><span class="fas fa-lock text-white fs-6"></span></div>
            <p class="mb-0 flex-1">
              <span class="fw-bold">Showing only {{ dailyResults.length }} of {{ totalAssets }} assets at random 👀</span><br>
              Your 60 days free Trend Radar access has expired.<br>
              Subscribe to unlock the full Trend Radar report with all assets and daily updates.<br>
              <router-link :to="{name: 'Subscription'}" class="btn btn-primary btn-sm mt-2">
                <span class="fas fa-gem me-1"></span> Subscribe Now
              </router-link>
            </p>
          </div>

          <!-- Info Alert (only for active users) -->
          <div v-if="!isLimited" class="alert alert-primary border-0 d-flex align-items-center mt-3" role="alert">
            <div class="bg-info me-3 icon-item"><span class="fas fa-info-circle text-white fs-6"></span></div>
            <div class="mb-0 flex-1">
              <span class="fw-bold">Example:</span>
              <ol class="mb-2 mt-1">
                <li>Sort by <span class="fw-bold">Trend Flip</span> descending</li>
                <li><span class="fw-bold">Scan top down</span> to find an asset that <span class="fw-bold">flipped Green</span> today/recently</li>
                <li>For those assets, check if there was a <span class="fw-bold">Breakout</span> recently</li>
                <li>If <span class="fw-bold">both signals are recent</span> and the <span class="fw-bold">price didn't yet pump</span>, it's a great opportunity</li>
                <li>Optional: Click on the chart icon to open the chart</li>
                <li><span class="fw-bold">Evaluate the asset with your strategies</span> and decide which one to use on this asset</li>
                <li><span class="fw-bold">Automate strategy + asset</span> by creating a <router-link :class="'fw-bold'" :to="{name: 'CreateNewBot', params: {}}">Trading Bot</router-link> ✅</li>
              </ol>
              This is not financial advice. You decide, which asset + strategy you use and how much you invest.
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Forward paper book (owner-only: user IDs 1 & 2; server also gates).
         The page owns the layout + spacing; the component is just the box. -->
    <div v-if="showPaperBooks" class="row justify-content-center mb-3">
      <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
        <PaperBookPanel />
      </div>
    </div>

    <!-- Video -->
    <div class="row justify-content-center mb-3">
      <div class="col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12">
        <div class="card mb-3">
          <div class="card-header">
            <div class="row flex-between-center">
              <div class="col-auto">
                <h5 class="fs-6 mb-0 text-nowrap py-2 py-xl-0">How to use the Trend Radar</h5>
              </div>
            </div>
          </div>
          <div class="card-body text-center">
            <iframe width="100%" height="450" style="max-width: 800px;" src="https://www.youtube-nocookie.com/embed/6G17MbjeX8c?si=tcddGZmmqrc8Pjth" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
            <p>
              <a href="https://www.youtube.com/@signum-app" target="_blank">Watch more tutorials on our YouTube Channel <span class="fab fa-youtube" data-fa-transform="right-0 down-0"></span></a>
            </p>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import TheNavbar from '@/components/TheNavbar'
import TheSubscriptionAlert from '@/components/TheSubscriptionAlert'
import TrendRadarChart from '@/components/TrendRadarChart'
import TrendRadarTable from '@/components/TrendRadarTable'
import PaperBookPanel from '@/components/PaperBookPanel'
import { userHelperMixin } from '@/mixins/userHelperMixin'
import { apiFetch } from '@/api/apiFetch'

export default {
  name: 'PageTrendRadar',
  mixins: [userHelperMixin],
  components: {
    TheNavbar,
    TheSubscriptionAlert,
    TrendRadarChart,
    TrendRadarTable,
    PaperBookPanel
  },
  data () {
    return {
      selectedAssetClass: 'crypto',
      selectedDetector: null,
      allAssetClasses: ['crypto', 'stocks', 'metals', 'commodities'],
      allDetectors: ['gc', 'b2s'],
      dailyResults: [],
      historicData: [],
      dailyRunDate: null,
      dailyRunTime: null,
      placeholderData: [{ runDate: '0000-00-00', green: 0, grey: 0, red: 0, total: 0 }],
      b2sExpanded: false,
      countdownText: '',
      countdownTimer: null,
      isLimited: false,
      totalAssets: 0
    }
  },
  computed: {
    isAutoTrading () {
      return this.userProfileMix?.isAutotradingUser || false
    },
    showTeaser () {
      // Non-AT user trying to access B2S or non-crypto
      if (this.selectedDetector === 'b2s' && !this.isAutoTrading) return true
      if (this.selectedAssetClass !== 'crypto' && !this.isAutoTrading) return true
      return false
    },
    showNoDataMessage () {
      // Valid user but combo doesn't exist (e.g. GC on stocks)
      return !this.showTeaser && this.selectedDetector === 'gc' && this.selectedAssetClass !== 'crypto'
    },
    showPaperBooks () {
      // Owner-only. The API also gates by user id, so this is just to hide the UI for everyone else; it is not the security boundary.
      return [1, 2].includes(this.userProfileMix?.id)
    },
    assetsPerAssetClassHtml () {
      if (this.isLimited && this.totalAssets > 0) return this.totalAssets
      return this.dailyResults.length || '...'
    },
    lastUpdatedText () {
      if (!this.dailyRunDate) return ''
      // Time is parsed verbatim from the run timestamp (server writes it in
      // UTC), so we always label it "UTC". If no time is available (e.g. an
      // old row with an empty `changed`), fall back to the date alone.
      return this.dailyRunTime ? `${this.dailyRunDate} ${this.dailyRunTime} UTC` : this.dailyRunDate
    }
  },
  methods: {
    selectAssetClass (ac) {
      if (ac === this.selectedAssetClass) return
      this.selectedAssetClass = ac
      if (!this.showTeaser && !this.showNoDataMessage) {
        this.fetchData()
      }
    },
    selectDetector (d) {
      if (d === this.selectedDetector) return
      this.selectedDetector = d
      if (!this.showTeaser && !this.showNoDataMessage) {
        this.fetchData()
      }
    },
    formatAssetClass (ac) {
      let formattedAc = ac.toLowerCase()
      formattedAc = (formattedAc == 'stocks') ? 'Stocks (US)' : formattedAc
      return formattedAc.charAt(0).toUpperCase() + formattedAc.slice(1)
    },
    extractRunTimeUtc (changed) {
      // `changed` is the run's write timestamp (ISO 8601, e.g.
      // "2026-06-01T00:09:43.512"). The trendradar container runs in UTC, so
      // we read HH:MM straight out of the string rather than going through
      // `new Date()` — a naive (offset-less) ISO string would otherwise be
      // interpreted as the viewer's local time and shifted. Returns null when
      // no time component is present (old/seeded rows with empty `changed`).
      if (typeof changed !== 'string') return null
      const m = changed.match(/T(\d{2}):(\d{2})/)
      return m ? `${m[1]}:${m[2]}` : null
    },
    updateCountdown () {
      const now = new Date()
      // Next update is 00:10 UTC (cron at 00:00 + ~10 min processing)
      const next = new Date(now)
      next.setUTCHours(0, 10, 0, 0)
      if (next <= now) next.setUTCDate(next.getUTCDate() + 1)

      let diff = Math.floor((next - now) / 1000)
      const h = Math.floor(diff / 3600)
      diff %= 3600
      const m = Math.floor(diff / 60)
      const s = diff % 60

      const parts = []
      if (h > 0) parts.push(h + 'h')
      if (m > 0 || h > 0) parts.push(m + 'm')
      parts.push(s + 's')
      this.countdownText = parts.join(' ')
    },
    async fetchData () {
      const detector = this.selectedDetector
      const assetClass = this.selectedAssetClass
      const baseUrl = process.env.VUE_APP_API_URL

      // Per apiFetch's contract: HTTP errors (including 401) come back as
      // Response objects, NOT thrown. 401 is also handled centrally by
      // apiFetch (logout + redirect). The catch below only fires on real
      // network failures (offline, DNS, CORS), which is what we want.
      try {
        const [dailyRes, historicRes] = await Promise.all([
          apiFetch(baseUrl + '/trendradar/daily?detector=' + detector + '&assetClass=' + assetClass, {
            method: 'GET'
          }),
          apiFetch(baseUrl + '/trendradar/historic?detector=' + detector + '&assetClass=' + assetClass + '&days=100', {
            method: 'GET'
          })
        ])

        // Guard: discard results if user switched tabs while fetch was in-flight
        if (this.selectedDetector !== detector || this.selectedAssetClass !== assetClass) return

        if (dailyRes.status === 200) {
          const dailyData = await dailyRes.json()
          if (dailyData.limited) {
            // Inactive subscription: API returns { limited, total, results }
            this.isLimited = true
            this.totalAssets = dailyData.total
            this.dailyResults = dailyData.results
          } else {
            // Active subscription: API returns plain array
            this.isLimited = false
            this.totalAssets = 0
            this.dailyResults = dailyData
          }
        } else {
          this.dailyResults = []
          this.isLimited = false
          this.totalAssets = 0
        }

        this.historicData = historicRes.status === 200 ? await historicRes.json() : []
        this.dailyRunDate = this.dailyResults.length > 0 ? this.dailyResults[0].runDate : null
        this.dailyRunTime = this.dailyResults.length > 0 ? this.extractRunTimeUtc(this.dailyResults[0].changed) : null
      } catch (e) {
        // Network failure (offline, DNS, CORS) — clear UI state so the
        // user sees the empty state instead of stale data.
        console.error('TrendRadar network error:', e)
        this.dailyResults = []
        this.historicData = []
        this.dailyRunDate = null
        this.dailyRunTime = null
        this.isLimited = false
        this.totalAssets = 0
      }
    }
  },
  async created () {
    this.updateCountdown()
    this.countdownTimer = setInterval(this.updateCountdown, 1000)

    this.selectedDetector = this.isAutoTrading ? 'b2s' : 'gc'
    if (!this.showTeaser && !this.showNoDataMessage) {
      await this.fetchData()
    }
  },
  beforeMount () {
    if (!this.$store.getters.isUserLoggedIn) this.$router.push({ name: 'Login' })
  },
  beforeUnmount () {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
  }
}
</script>
